import { FC } from "react";
import { StepsProps } from "../../types";
import { Button } from "@/components/ui";
import { FormProvider } from "react-hook-form";
import useStep1 from "./useStep1";
import {
  FormInput,
  FormPhoneInput,
  FormSectionHeader,
  FormSelect,
  Loader,
} from "@/components";
import {
  ACCOUNTING_METHOD,
  accountingMethodOptions,
  ADDRESS_1,
  ADDRESS_2,
  AUDIT_SCOPE,
  AUDIT_STATE,
  auditScopeOptions,
  CITY,
  COMMUNITY_NAME,
  COMMUNITY_REGISTERED_NAME,
  CONTACT_EMAIL,
  COUNTY,
  EMERGENCY_PHONE,
  GENERAL_PHONE,
  LANGUAGE,
  LICENSE_CATEGORY,
  licenseCategoryOptions,
  MANAGER_NAME,
  MANAGER_PHONE,
  STATE,
  UNITS,
  WEBSITE,
  ZIPCODE,
} from "./consts";

const Step1: FC<StepsProps> = ({ onCancel, isView, onNextClick }) => {
  const {
    form,
    onSubmit,
    requiredFields,
    isAddPending,
    stateList,
    managerList,
    isUpdatePending,
    isManagerLoading,
    isEditDetails,
    keys,
    isDetailLoading,
    isDetailRefetching,
  } = useStep1({ onNextClick });
  const { errors } = form.formState;

  return (
    <>
      {isManagerLoading || isDetailLoading || isDetailRefetching ? (
        <Loader />
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
              <div>
                <FormSectionHeader>Community Information</FormSectionHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                  <FormInput
                    name={COMMUNITY_NAME}
                    control={form.control}
                    label="Name"
                    placeholder="Please enter"
                    error={errors[COMMUNITY_NAME]?.message}
                    isRequired={requiredFields.includes(COMMUNITY_NAME)}
                    disabled={isView}
                    maxLength={30}
                    variant="char"
                  />
                  <FormInput
                    name={COMMUNITY_REGISTERED_NAME}
                    control={form.control}
                    label="Community Registered Name"
                    placeholder="Please enter"
                    error={errors[COMMUNITY_REGISTERED_NAME]?.message}
                    isRequired={requiredFields.includes(
                      COMMUNITY_REGISTERED_NAME
                    )}
                    disabled={isView}
                    minLength={3}
                    maxLength={30}
                  />
                  <FormInput
                    name={LANGUAGE}
                    control={form.control}
                    label="Language"
                    placeholder="Please enter"
                    error={errors[LANGUAGE]?.message}
                    isRequired={requiredFields.includes(LANGUAGE)}
                    disabled
                  />
                  <FormInput
                    name={UNITS}
                    control={form.control}
                    label="Units"
                    placeholder="Please enter"
                    error={errors[UNITS]?.message}
                    isRequired={requiredFields.includes(UNITS)}
                    disabled={isView}
                    variant="integer"
                    maxLength={4}
                  />
                  <FormSelect
                    showSearch={false}
                    key={keys.scopeKey}
                    name={AUDIT_SCOPE}
                    control={form.control}
                    label="Audit Scope"
                    placeholder="Please select"
                    error={errors[AUDIT_SCOPE]?.message}
                    isRequired={requiredFields.includes(AUDIT_SCOPE)}
                    options={auditScopeOptions}
                    disabled={isView}
                  />
                  {form.watch(AUDIT_SCOPE) === "STATE" && (
                    <FormSelect
                      key={keys.stateKey}
                      name={AUDIT_STATE}
                      control={form.control}
                      label="Audit State"
                      placeholder="Please select"
                      error={errors[AUDIT_STATE]?.message}
                      isRequired={form.watch(AUDIT_SCOPE) === "STATE"}
                      options={stateList?.map((data: any) => ({
                        label: data.name,
                        value: data.code,
                      }))}
                      disabled={isView}
                    />
                  )}
                  <FormSelect
                    showSearch={false}
                    key={keys.licenseCatKey}
                    name={LICENSE_CATEGORY}
                    control={form.control}
                    label="License Category"
                    placeholder="Please select"
                    error={errors[LICENSE_CATEGORY]?.message}
                    isRequired={requiredFields.includes(LICENSE_CATEGORY)}
                    options={licenseCategoryOptions}
                    disabled={isView}
                  />
                  <FormSelect
                    showSearch={false}
                    key={keys.accMethodKey}
                    name={ACCOUNTING_METHOD}
                    control={form.control}
                    label="Accounting Method"
                    placeholder="Please select"
                    error={errors[ACCOUNTING_METHOD]?.message}
                    isRequired={requiredFields.includes(ACCOUNTING_METHOD)}
                    options={accountingMethodOptions}
                    disabled={isView}
                  />
                </div>
              </div>

              <div className="mt-[40px]">
                <FormSectionHeader>
                  Mailing and Contact Information
                </FormSectionHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                  <FormInput
                    name={ADDRESS_1}
                    control={form.control}
                    label="Address 1"
                    placeholder="Please enter"
                    error={errors[ADDRESS_1]?.message}
                    isRequired={requiredFields.includes(ADDRESS_1)}
                    disabled={isView}
                  />
                  <FormInput
                    name={ADDRESS_2}
                    control={form.control}
                    label="Address 2"
                    placeholder="Please enter"
                    error={errors[ADDRESS_2]?.message}
                    isRequired={requiredFields.includes(ADDRESS_2)}
                    disabled={isView}
                  />
                  <FormInput
                    name={ZIPCODE}
                    control={form.control}
                    label="Zipcode"
                    placeholder="Please enter"
                    error={errors[ZIPCODE]?.message}
                    isRequired={requiredFields.includes(ZIPCODE)}
                    maxLength={10}
                    variant="integerSpeChar"
                    disabled={isView}
                  />
                  <FormSelect
                    key={keys.stateKey}
                    name={STATE}
                    control={form.control}
                    label="State"
                    placeholder="Please select"
                    error={errors[STATE]?.message}
                    isRequired={requiredFields.includes(STATE)}
                    options={stateList.map((data: any) => ({
                      label: data.name,
                      value: data.code,
                    }))}
                    disabled={isView}
                  />
                  <FormInput
                    key={keys.cityKey}
                    name={CITY}
                    control={form.control}
                    label="City"
                    placeholder="Please enter"
                    error={errors[CITY]?.message}
                    isRequired={requiredFields.includes(CITY)}
                    disabled={isView}
                    variant="char"
                  />
                  <FormInput
                    name={COUNTY}
                    control={form.control}
                    label="County"
                    placeholder="Please enter"
                    error={errors[COUNTY]?.message}
                    isRequired={requiredFields.includes(COUNTY)}
                    disabled={isView}
                  />
                  <FormPhoneInput
                    name={GENERAL_PHONE}
                    control={form.control}
                    label="General Contact Phone"
                    placeholder="Please enter"
                    error={errors[GENERAL_PHONE]?.message}
                    isRequired={requiredFields.includes(GENERAL_PHONE)}
                    disabled={isView}
                  />
                  <FormPhoneInput
                    name={EMERGENCY_PHONE}
                    control={form.control}
                    label="Emergency Contact Phone"
                    placeholder="Please enter"
                    error={errors[EMERGENCY_PHONE]?.message}
                    isRequired={requiredFields.includes(EMERGENCY_PHONE)}
                    disabled={isView}
                  />
                  <FormSelect
                    key={keys.managerKey}
                    name={MANAGER_NAME}
                    control={form.control}
                    label="Community Manager Name"
                    placeholder="Please enter"
                    error={errors[MANAGER_NAME]?.message}
                    isRequired={requiredFields.includes(MANAGER_NAME)}
                    options={(managerList?.data || []).map(
                      (user: { userId: string; name: string; userName: string }) => {
                        return {
                          label: `${user.name || ""} (${user.userName})`,
                          value: user.userId.toString() || "",
                        };
                      }
                    )}
                    disabled={isView}
                  />
                  <FormPhoneInput
                    name={MANAGER_PHONE}
                    control={form.control}
                    label="Community Manager Phone"
                    placeholder="Please enter"
                    error={errors[MANAGER_PHONE]?.message}
                    isRequired={requiredFields.includes(MANAGER_PHONE)}
                    disabled
                  />
                  <FormInput
                    name={CONTACT_EMAIL}
                    control={form.control}
                    label="Contact Email"
                    placeholder="Please enter"
                    error={errors[CONTACT_EMAIL]?.message}
                    isRequired={requiredFields.includes(CONTACT_EMAIL)}
                    disabled={isView}
                  />
                  <FormInput
                    name={WEBSITE}
                    control={form.control}
                    label="Website"
                    placeholder="Please enter"
                    error={errors[WEBSITE]?.message}
                    isRequired={requiredFields.includes(WEBSITE)}
                    disabled={isView}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-row justify-end gap-3  py-3">
              <Button variant="outline" type="button" onClick={onCancel}>
                Cancel
              </Button>
              {!isView && (
                <Button type="submit">
                  {isAddPending ||
                    (isUpdatePending && <Loader variant="secondary" />)}
                  {isEditDetails ? "Save" : "Save & Next"}
                </Button>
              )}
            </div>
          </form>
        </FormProvider>
      )}
    </>
  );
};

export default Step1;
